import React, { useState, useEffect } from 'react';
import AIResponseCard from '../components/AIResponseCard';
import { processInnovaTechHypothesis } from '../services/aiService';
import { Users, BarChart3, CheckCircle, Play } from 'lucide-react';

// 🧪 Página de pruebas para validar todos los roles IA
const AIRolesTestPage = () => {
    const [results, setResults] = useState({});
    const [isProcessing, setIsProcessing] = useState({});

    // 🎭 Casos de prueba para cada rol
    const testCases = [
        {
            id: 'tutor',
            title: 'ROL TUTOR - Análisis de Hipótesis',
            context: {
                lab_id: "lab1",
                step_id: "hipotesis_central",
            },
            input: "Creemos que estudiantes universitarios de ingeniería tienen dificultades para encontrar prácticas profesionales relevantes porque las plataformas actuales no filtran por especialización técnica.",
            expectedRole: 'tutor',
            icon: Play
        },
        {
            id: 'sparring_cliente',
            title: 'ROL SPARRING CLIENTE - Simulación Escéptico',
            context: {
                lab_id: "lab2", 
                step_id: "sparring",
                cliente_perfil: "escéptico"
            },
            input: "Te presento nuestra app que conecta estudiantes con empresas para prácticas profesionales. Reducimos el tiempo de búsqueda en 60% y tenemos 500 estudiantes registrados.",
            expectedRole: 'sparring_cliente',
            icon: Users
        },
        {
            id: 'analista',
            title: 'ROL ANALISTA - Interpretación de Métricas',
            context: {
                lab_id: "lab3",
                step_id: "analisis"
            },
            input: "Entrevistamos 25 estudiantes de ingeniería: 20 confirmaron que tienen dificultades para encontrar prácticas, 3 no lo ven como problema, 2 ya tienen prácticas aseguradas. El 80% usa LinkedIn, 60% usa bolsas de trabajo universitarias.",
            expectedRole: 'analista',
            icon: BarChart3
        },
        {
            id: 'evaluador',
            title: 'ROL EVALUADOR - Evaluación de Progreso',
            context: {
                lab_id: "lab4",
                step_id: "evaluacion"
            },
            input: "Hemos completado validación de hipótesis (Lab1), simulaciones con 3 perfiles de cliente (Lab2), y análisis de 25 entrevistas con métricas claras (Lab3). ¿Cuál sería el siguiente paso recomendado?",
            expectedRole: 'evaluador',
            icon: CheckCircle
        }
    ];

    // 🚀 Ejecutar prueba de un rol específico
    const runTest = async (testCase) => {
        const testId = testCase.id;
        setIsProcessing(prev => ({ ...prev, [testId]: true }));
        
        try {
            console.log(`🧪 [Test ${testCase.expectedRole}] Iniciando...`);
            console.log(`📝 Input:`, testCase.input);
            console.log(`📋 Context:`, testCase.context);

            const result = await processInnovaTechHypothesis({
                hypothesis: testCase.input,
                lab_id: testCase.context.lab_id,
                step_id: testCase.context.step_id,
                session_id: `test_${testId}_${Date.now()}`
            });

            console.log(`✅ [Test ${testCase.expectedRole}] Resultado:`, result);

            setResults(prev => ({
                ...prev,
                [testId]: {
                    success: true,
                    content: result.content || "Respuesta procesada",
                    role: result.role || "desconocido",
                    source: result.source || "fallback",
                    timestamp: Date.now(),
                    expectedRole: testCase.expectedRole,
                    roleMatch: result.role === testCase.expectedRole
                }
            }));

        } catch (error) {
            console.error(`❌ [Test ${testCase.expectedRole}] Error:`, error);
            
            setResults(prev => ({
                ...prev,
                [testId]: {
                    success: false,
                    error: error.message,
                    timestamp: Date.now(),
                    expectedRole: testCase.expectedRole,
                    roleMatch: false
                }
            }));
        } finally {
            setIsProcessing(prev => ({ ...prev, [testId]: false }));
        }
    };

    // 🎯 Ejecutar todas las pruebas
    const runAllTests = async () => {
        console.log('🚀 [AI Roles Test] Ejecutando todos los tests...');
        
        for (const testCase of testCases) {
            await runTest(testCase);
            // Pausa entre tests para evitar rate limiting
            await new Promise(resolve => setTimeout(resolve, 1000));
        }
        
        console.log('✅ [AI Roles Test] Todos los tests completados');
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-indigo-50 py-8">
            <div className="container mx-auto px-4 max-w-6xl">
                {/* 🎯 Header */}
                <div className="text-center mb-8">
                    <h1 className="text-3xl font-bold text-gray-800 mb-4">
                        🧪 Validación Completa - 4 Roles IA
                    </h1>
                    <p className="text-gray-600 mb-6">
                        Prueba sistemática de todos los roles implementados según especificaciones del experto
                    </p>
                    
                    <button
                        onClick={runAllTests}
                        className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all"
                    >
                        🚀 Ejecutar Todos los Tests
                    </button>
                </div>

                {/* 🎭 Grid de tests */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                    {testCases.map((testCase) => (
                        <div key={testCase.id} className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
                            {/* Header del test */}
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-3">
                                    <testCase.icon className="w-6 h-6 text-indigo-600" />
                                    <h3 className="font-semibold text-gray-800">{testCase.title}</h3>
                                </div>
                                <button
                                    onClick={() => runTest(testCase)}
                                    disabled={isProcessing[testCase.id]}
                                    className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 disabled:bg-gray-400 transition-all text-sm"
                                >
                                    {isProcessing[testCase.id] ? 'Testing...' : 'Test Individual'}
                                </button>
                            </div>

                            {/* Input del test */}
                            <div className="mb-4">
                                <div className="text-xs font-medium text-gray-500 mb-1">INPUT:</div>
                                <div className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg">
                                    {testCase.input.substring(0, 150)}...
                                </div>
                            </div>

                            {/* Context */}
                            <div className="mb-4">
                                <div className="text-xs font-medium text-gray-500 mb-1">CONTEXT:</div>
                                <div className="text-xs text-gray-600 font-mono">
                                    lab_id: {testCase.context.lab_id}, step_id: {testCase.context.step_id}
                                </div>
                            </div>

                            {/* Status */}
                            <div className="flex items-center gap-2">
                                <span className="text-xs font-medium text-gray-500">ESTADO:</span>
                                {results[testCase.id] ? (
                                    <span className={`text-xs px-2 py-1 rounded ${
                                        results[testCase.id].success && results[testCase.id].roleMatch
                                            ? 'bg-green-100 text-green-700'
                                            : 'bg-red-100 text-red-700'
                                    }`}>
                                        {results[testCase.id].success && results[testCase.id].roleMatch
                                            ? '✅ PASÓ' 
                                            : '❌ FALLÓ'
                                        }
                                    </span>
                                ) : (
                                    <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded">
                                        Pendiente
                                    </span>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                {/* 📊 Resultados */}
                {Object.keys(results).length > 0 && (
                    <div className="space-y-6">
                        <h2 className="text-2xl font-bold text-gray-800 mb-4">📊 Resultados de las Pruebas</h2>
                        
                        {testCases.map((testCase) => {
                            const result = results[testCase.id];
                            if (!result) return null;

                            return (
                                <div key={testCase.id}>
                                    {result.success ? (
                                        <AIResponseCard
                                            response={result.content}
                                            aiRole={result.role}
                                            aiSource={result.source}
                                        />
                                    ) : (
                                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                            <div className="flex items-center gap-2 text-red-700 font-semibold mb-2">
                                                <span>❌ Error en {testCase.title}</span>
                                            </div>
                                            <div className="text-red-600 text-sm">{result.error}</div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* 📈 Resumen */}
                {Object.keys(results).length === testCases.length && (
                    <div className="mt-8 bg-white rounded-xl shadow-lg border border-gray-200 p-6">
                        <h3 className="font-semibold text-gray-800 mb-4">📈 Resumen de Validación</h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {testCases.map((testCase) => {
                                const result = results[testCase.id];
                                return (
                                    <div key={testCase.id} className="text-center">
                                        <div className="text-sm font-medium text-gray-600 mb-1">{testCase.expectedRole}</div>
                                        <div className={`text-2xl ${
                                            result?.success && result?.roleMatch 
                                                ? 'text-green-600' 
                                                : 'text-red-600'
                                        }`}>
                                            {result?.success && result?.roleMatch ? '✅' : '❌'}
                                        </div>
                                        <div className="text-xs text-gray-500">
                                            {result?.role || 'N/A'}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AIRolesTestPage;